import React, { useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import WorldLabel from './WorldLabel';
import { useWorldStore } from '../state/worldStore';
import { usePov, useHasBody } from '../hooks/usePov';
import { authUser, PHONE_IN_HAND } from '../config';
import PhoneScreen from './phone/PhoneScreen';

// PhoneInHand — the phone as an object in the world, for FIRST person.
//
// Third person puts it in the HUD (see <Phone/>): you are looking at your
// character, so the phone is yours and belongs on your screen. First person is
// the other way round — you are looking through their eyes, so the phone is
// theirs and belongs in their hand. Same screen either way; only the placement
// changes, which is why PhoneScreen has no opinion about where it is.
//
// REAL DOM, POSITIONED IN 3D. Live DOM cannot be a WebGL texture, so a phone
// screen painted onto a mesh could only ever be a still image or a video — the
// apps would stop being apps. drei's <Html transform> renders the actual React
// tree with a CSS3D transform instead, so every screen keeps working untouched
// and simply arrives at an angle. That constraint is why this is a placement
// change rather than a rewrite.
//
// It rides the CAMERA rather than a hand bone. A bone would be more literal, but
// the rigged bodies are shared animation clips with no phone-holding pose, so
// the phone would swing with whatever the arm happened to be doing. A viewmodel
// offset is what shooters do for the same reason: the object belongs to the
// view, not to the animation.

// TUNE LIVE, no rebuild — the same way every other placement in this world is
// dialled in. Held-object framing is the kind of thing you cannot get right by
// reasoning about numbers; it has to be looked at.
//   ?handscale=0.34   metres per CSS pixel (apparent size)
//   ?handx / ?handy / ?handz   raised offset from the camera, in metres
const q = (k, d) => {
  try {
    const v = parseFloat(new URLSearchParams(window.location.search).get(k));
    return Number.isFinite(v) ? v : d;
  } catch { return d; }
};

// Local offset from the camera, in metres — down and to the right, tilted back
// toward the face like something actually being held.
const RAISED = { pos: [q('handx', 0.17), q('handy', -0.16), q('handz', -0.52)], rot: [-0.16, -0.22, 0.05] };
// Lowered still leaves the top edge in frame, so there is something to look at
// and click. Dropping it out of view entirely would leave first person with no
// discoverable way to raise it at all.
const LOWERED = { pos: [0.2, -0.46, -0.62], rot: [-1.05, -0.26, 0.06] };

const LERP = 0.18;

export default function PhoneInHand() {
  const rig = useRef();
  const holder = useRef();
  const { camera } = useThree();

  const phoneOpen = useWorldStore((s) => s.phoneOpen);
  const togglePhone = useWorldStore((s) => s.togglePhone);
  const myAvatarId = useWorldStore((s) => s.myAvatarId);
  const pov = usePov();
  const hasBody = useHasBody();
  const { isAdmin } = authUser();

  useFrame(() => {
    if (!rig.current || !holder.current) return;
    // Lock the rig to the camera each frame, then let the phone sit at a fixed
    // local offset inside it — the standard viewmodel trick, and the only one
    // that works regardless of whether the camera is in the scene graph.
    rig.current.position.copy(camera.position);
    rig.current.quaternion.copy(camera.quaternion);

    const t = phoneOpen ? RAISED : LOWERED;
    const h = holder.current;
    h.position.lerp({ x: t.pos[0], y: t.pos[1], z: t.pos[2] }, LERP);
    h.rotation.x += (t.rot[0] - h.rotation.x) * LERP;
    h.rotation.y += (t.rot[1] - h.rotation.y) * LERP;
    h.rotation.z += (t.rot[2] - h.rotation.z) * LERP;
  });

  // PARKED. The held presentation is unfinished — a CSS3D panel floating at a
  // viewmodel offset reads as a rendering fault rather than as a phone someone
  // is holding. Off by default, ?phonehand=1 to work on it. Phone.jsx reads the
  // SAME flag when it chooses hud-vs-hand, so first person falls back to the HUD
  // phone rather than losing the phone entirely.
  if (!PHONE_IN_HAND) return null;
  if (!isAdmin || !myAvatarId) return null;
  if (pov !== 'first' || !hasBody) return null;   // no body, no hand — HUD keeps it

  return (
    <group ref={rig}>
      <group ref={holder} position={LOWERED.pos} rotation={LOWERED.rot}>
        <WorldLabel
          transform
          // Never occluded: a phone held at arm's length that a doorframe can
          // eat is worse than one that always reads.
          occlude={false}
          // Metres per CSS pixel — the device is ~300px wide and wants to be
          // roughly 8cm across in the world.
          distanceFactor={q('handscale', 0.34)}
          // The world must keep running behind it, so only the device itself
          // takes input; the wrapper stays transparent to clicks.
          style={{ pointerEvents: 'none' }}
          zIndexRange={[40, 0]}
        >
          <div
            style={{
              width: 300, height: 617, pointerEvents: 'auto',
              opacity: phoneOpen ? 1 : 0.92,
              transition: 'opacity 200ms ease',
            }}
          >
            <PhoneScreen onToggle={togglePhone} />
          </div>
        </WorldLabel>
      </group>
    </group>
  );
}
